import { z } from 'zod'
import { computed, type Ref } from 'vue'
import { useQuery } from '@fusapp-work/shell'
import { ApiError, type ApiClient } from '@fusapp-work/api'
import { parseResponse } from '../../../organization/shared/response'
import { hasControlChar, serverText } from './mailAddress'
import { providerFieldSchema } from './providerSettings'

export const channelTypes = ['SMS', 'Call', 'Email', 'Notification', 'PushNotification', 'Chat', 'MetaMessenger', 'Whatsapp', 'MicrosoftTeams'] as const
const filterTypes = [...channelTypes, 'Unknown'] as const
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
const pageNumber = z.number().int().min(1).max(10000)
const pageSize = z.number().int().min(1).max(100)
const directions = z.union([z.tuple([z.literal('Inbound')]), z.tuple([z.literal('Outbound')]), z.tuple([z.literal('Inbound'), z.literal('Outbound')])]).nullable()
export const channelIdSchema = z.string().regex(/^[a-f\d]{24}$/i)
export const channelDetailSchema = z.object({
  id: channelIdSchema, name: z.string().nullable(), code: z.string().nullable(), type: z.enum(channelTypes).nullable(),
  isEnabled: z.boolean().nullable(), isDefault: z.boolean().nullable(), providerId: z.string().nullable(), directions,
  senderAddress: z.string().nullable(),
})
export type ChannelDetail = z.infer<typeof channelDetailSchema>
const providerSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/).max(80), name: z.string().min(1),
  types: z.enum(channelTypes).array().min(1), configurationRequired: z.boolean(),
  canConfigure: z.boolean(), canTest: z.boolean(), canEnable: z.boolean(),
  supportsTesting: z.boolean().optional(), directions: z.enum(['Inbound', 'Outbound']).array().max(2).optional(),
  fields: providerFieldSchema.array().max(32).refine(value => new Set(value.map(field => field.key)).size === value.length &&
    value.filter(field => field.kind === 'secret').length <= 1).optional(),
})
const providersSchema = z.object({ items: providerSchema.array() }).refine(value => new Set(value.items.map(item => item.id)).size === value.items.length)
export type ChannelProvider = z.infer<typeof providerSchema>
export const channelCreateSchema = z.object({
  // Backend CreateValidator on the .NET-trimmed name: 2–120 characters, no char.IsControl.
  name: z.string().transform(serverText).pipe(z.string().min(2).max(120).refine(value => !hasControlChar(value))),
  code: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).min(2).max(64),
  type: z.enum(channelTypes), providerId: z.string().regex(/^[a-z][a-z0-9_]*$/).max(80),
})
export type ChannelCreateInput = z.input<typeof channelCreateSchema>
const createdSchema = z.object({ id: channelIdSchema })
const channelSchema = z.object({
  id: z.string().regex(/^[a-f\d]{24}$/i), name: z.string().nullable(), code: z.string().nullable(), type: z.enum(channelTypes).nullable(),
  isEnabled: z.boolean().nullable(), isDefault: z.boolean().nullable(), providerId: z.string().nullable(),
  providerName: z.string().min(1).max(120).nullable().optional(), isManaged: z.boolean().nullable().optional(), directions,
  senderAddress: z.string().nullable(), isMaster: z.boolean().nullable().optional(),
  activity7Days: count.nullable(), permanentErrors7Days: count.nullable(),
})
const summarySchema = z.object({
  channelCount: count, enabledChannelCount: count, typeCounts: z.partialRecord(z.enum(filterTypes), count),
  managedChannelCount: count.optional(), enabledManagedChannelCount: count.optional(), masterChannelCount: count.optional(),
  providerCount: count.nullable(), providerCountState: z.enum(['available', 'unavailable']),
  testableProviderCount: count.nullable().optional(), inboundProviderCount: count.nullable().optional(),
  outbound7Days: count.nullable(), inbound7Days: count.nullable(), permanentErrors7Days: count.nullable(),
  sevenDayMetricsState: z.enum(['available', 'unavailable']), metricsAsOf: z.iso.datetime({ offset: true }).nullable(),
}).refine(value => value.enabledChannelCount <= value.channelCount &&
  Object.values(value.typeCounts).reduce((sum, n) => sum + n, 0) === value.channelCount &&
  (value.managedChannelCount === undefined || value.managedChannelCount <= value.channelCount) &&
  (value.masterChannelCount === undefined || value.masterChannelCount <= value.channelCount) &&
  (value.enabledManagedChannelCount === undefined || value.managedChannelCount !== undefined && value.enabledManagedChannelCount <= value.managedChannelCount) &&
  (value.providerCountState !== 'available' || value.providerCount !== null) &&
  (value.providerCountState !== 'unavailable' || value.providerCount === null) &&
  (value.sevenDayMetricsState !== 'available' || value.metricsAsOf !== null) &&
  (value.sevenDayMetricsState !== 'unavailable' || value.metricsAsOf === null))
export const channelPageSchema = z.object({
  items: channelSchema.array(), page: pageNumber, pageSize, totalCount: count, totalPages: count, hasNextPage: z.boolean(), summary: summarySchema,
}).refine(value => value.totalPages === Math.ceil(value.totalCount / value.pageSize) && value.hasNextPage === (value.page < value.totalPages))
export type ChannelPage = z.infer<typeof channelPageSchema>
const filtersSchema = z.object({ page: pageNumber, pageSize, search: z.string().trim().max(100), type: z.union([z.literal(''), z.enum(filterTypes)]) })
export type ChannelFilters = z.infer<typeof filtersSchema>

/** Accept only scalar route values; organization context is intentionally ignored. */
export function parseChannelQuery(query: Record<string, unknown>) {
  const number = (value: unknown, fallback: number) => value === undefined ? fallback : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN
  return filtersSchema.safeParse({ page: number(query.page, 1), pageSize: number(query.pageSize, 25), search: query.search === undefined ? '' : query.search, type: query.type === undefined ? '' : query.type })
}
export function createChannelsApi(client: ApiClient) {
  return {
    async providers(signal?: AbortSignal) {
      return parseResponse(providersSchema, await client.request<unknown>('/api/v1/console/communication/channels/providers', { signal })).items
    },
    async create(input: ChannelCreateInput, signal?: AbortSignal) {
      const body = channelCreateSchema.parse(input)
      return parseResponse(createdSchema, await client.request<unknown>('/api/v1/console/communication/channels', {
        method: 'POST', body, headers: { 'X-Console-Request': '1' }, signal,
      }))
    },
    async detail(id: string, signal?: AbortSignal) {
      const input = channelIdSchema.parse(id)
      const value = parseResponse(channelDetailSchema, await client.request<unknown>(`/api/v1/console/communication/channels/${input}`, { signal }))
      if (value.id.toLowerCase() !== input.toLowerCase()) throw new ApiError('Invalid channel detail', 200, 'INVALID_RESPONSE')
      return value
    },
    async list(filters: ChannelFilters, signal?: AbortSignal) {
      const input = filtersSchema.parse(filters), query = new URLSearchParams()
      for (const [key, value] of Object.entries(input)) if (value !== '') query.set(key, String(value))
      const value = parseResponse(channelPageSchema, await client.request<unknown>(`/api/v1/console/communication/channels?${query}`, { signal }))
      if (value.page !== input.page || value.pageSize !== input.pageSize) throw new ApiError('Invalid channel page', 200, 'INVALID_RESPONSE')
      return value
    },
  }
}
export function channelCreateErrorKey(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'unauthorized'
    if (error.status === 403) return 'forbidden'
    if (error.code === 'CHANNEL_CODE_ALREADY_EXISTS') return 'duplicateCode'
    if (error.code === 'CONSOLE_CHANNEL_PROVIDER_UNSUPPORTED') return 'providerUnsupported'
    if (error.code === 'CONSOLE_CHANNEL_CREATE_OUTCOME_UNCERTAIN') return 'uncertain'
    if (error.code === 'CONSOLE_CHANNEL_CREATE_INVALID') return 'invalid'
    if (error.code === 'INVALID_RESPONSE') return 'invalidResponse'
  }
  return 'unavailable'
}
export function channelErrorKey(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'unauthorized'
    if (error.status === 403) return 'forbidden'
    if (error.code === 'CONSOLE_CHANNEL_QUERY_INVALID') return 'invalidQuery'
    if (error.code === 'INVALID_RESPONSE') return 'invalidResponse'
  }
  return 'unavailable'
}

/** Keep return state on the frontend route, never on the detail API request. */
export function channelRouteQuery(query: Record<string, unknown>) {
  const parsed = parseChannelQuery(query)
  const value = parsed.success ? parsed.data : { page: 1, pageSize: 25, search: '', type: '' }
  return { page: String(value.page), pageSize: String(value.pageSize), ...(value.search ? { search: value.search } : {}), ...(value.type ? { type: value.type } : {}) }
}
export function channelDetailErrorKey(error: unknown) {
  return error instanceof ApiError && error.status === 404 ? 'notFound' : channelErrorKey(error)
}
export function useChannelDetail(client: ApiClient, principal: Readonly<Ref<string | undefined>>, id: Readonly<Ref<string | undefined>>) {
  const api = createChannelsApi(client)
  return useQuery({
    queryKey: computed(() => ['console', principal.value, 'channel-detail', id.value]),
    queryFn: ({ signal }) => api.detail(id.value!, signal),
    enabled: computed(() => Boolean(principal.value && id.value)),
    retry: false, refetchOnWindowFocus: false,
  })
}
